import { modalType as MODAL_TYPE } from '@/components/Modals/modalType';
import { NDE_CONFECTION } from '@/utils/categoryUtils';

type ParentProductId = number;
type ConfectionCategory = string;
type ConfectionSlug = string;
type ModalType = string;
type ModalTypeByCategory = Record<ConfectionCategory, ModalType>;
type ModalImageByModalType = Record<ModalType, string>;
type ConfectionImageByCategory = Record<ConfectionCategory, string>;
type ConfectionImageBySlug = Record<ConfectionSlug, string>;

const DEFAULT_MODAL_TYPE_BY_CATEGORY: ModalTypeByCategory = {
  [NDE_CONFECTION.ETUI]: MODAL_TYPE.NDE_ETUI_CARD,
  [NDE_CONFECTION.ETUI_PUT_IN]: MODAL_TYPE.NDE_ETUI_CARD_PUT_IN,
  [NDE_CONFECTION.ETUI_PERSONALISATION]: MODAL_TYPE.NDE_ETUI_CARD_PERSONALISATION,
  [NDE_CONFECTION.ENVELOPE_PERSONALISATION]: MODAL_TYPE.NDE_ENVELOPE_CARD_PERSONALISATION,
  [NDE_CONFECTION.PROMO_PACKET]: MODAL_TYPE.NDE_PROMO_PACKET,
};

const BN_MODAL_TYPE_BY_CATEGORY: ModalTypeByCategory = {
  [NDE_CONFECTION.ETUI]: MODAL_TYPE.NDE_ETUI_CARD_CHRISTMAS,
};

const PRODUCT_ID_MODAL_TYPE_OVERRIDES_BY_CATEGORY: Record<ParentProductId, ModalTypeByCategory> = {
  30702: BN_MODAL_TYPE_BY_CATEGORY,
  30937: BN_MODAL_TYPE_BY_CATEGORY,
};

const MODAL_IMAGE_BY_MODAL_TYPE: ModalImageByModalType = {
  [MODAL_TYPE.ENVELOPE_COUPON]: 'ESPI_coupon_envelope.png',
  [MODAL_TYPE.ENVELOPE_COUPON_PUT_IN]: 'ESPI_envelope_put_in_pluxee_new.png',
  [MODAL_TYPE.ENVELOPE_COUPON_PERSONALISATION]: 'ESPI_nde_envelope_perso_pluxee_new.png',
  [MODAL_TYPE.NDE_ETUI_CARD]: 'ESPI_nde_etui_pluxee_new.png',
  [MODAL_TYPE.NDE_ETUI_CARD_CHRISTMAS]: 'ESPI_etui_swieta_BN26.png',
  [MODAL_TYPE.NDE_ETUI_CARD_PUT_IN]: 'ESPI_nde_etui_put_in_pluxee_new.png',
  [MODAL_TYPE.NDE_ETUI_CARD_PERSONALISATION]: 'ESPI_nde_etui_perso_pluxee_new.png',
  [MODAL_TYPE.NDE_ENVELOPE_CARD_PERSONALISATION]: 'ESPI_nde_envelope_perso_pluxee_new.png',
  [MODAL_TYPE.NDE_PROMO_PACKET]: 'promo_package_BN24.png',
  [MODAL_TYPE.NDE_APP_CONFECTION]: 'ESPI_nde_app_confection.png',
};

const BN_MODAL_IMAGE_BY_MODAL_TYPE: ModalImageByModalType = {
  [MODAL_TYPE.NDE_ETUI_CARD_PUT_IN]: 'ESPI_etui_put_in_BN26.png',
  [MODAL_TYPE.NDE_ETUI_CARD_PERSONALISATION]: 'ESPI_etui_perso_BN26.png',
  [MODAL_TYPE.NDE_ENVELOPE_CARD_PERSONALISATION]: 'ESPI_envelope_perso_BN26.png',
};

const PRODUCT_ID_MODAL_IMAGE_OVERRIDES_BY_MODAL_TYPE: Record<
  ParentProductId,
  ModalImageByModalType
> = {
  30702: BN_MODAL_IMAGE_BY_MODAL_TYPE,
  30937: BN_MODAL_IMAGE_BY_MODAL_TYPE,
};

const INCENTIVE_MODAL_IMAGE_BY_CATEGORY: ConfectionImageByCategory = {
  [NDE_CONFECTION.PROMO_PACKET]: 'promo_package_BN24.png',
  [NDE_CONFECTION.ETUI]: 'ESPI_nde_etui_pluxee_incentive_new.png',
  [NDE_CONFECTION.ETUI_PUT_IN]: 'ESPI_nde_etui_put_in_pluxee_new.png',
  [NDE_CONFECTION.ETUI_PERSONALISATION]: 'ESPI_nde_etui_perso_pluxee_new.png',
  [NDE_CONFECTION.ENVELOPE_PERSONALISATION]: 'ESPI_nde_envelope_perso_pluxee_new.png',
};

const INCENTIVE_MODAL_IMAGE_BY_SLUG: ConfectionImageBySlug = {
  'ozdobne-swiateczne-etui-na-karte': 'ESPI_nde_etui_pluxee_christmas.png',
  'personalizacja-nadruku-na-kopercie': 'ESPI_nde_envelope_perso_pluxee_new.png',
  'personalizacja-nadruku-na-etui': 'ESPI_nde_etui_perso_pluxee_new.png',
  'wlozenie-karty-do-ozdobnego-etui': 'ESPI_nde_etui_put_in_pluxee_new.png',
  'koperty-ozdobne-do-kuponow': 'ESPI_coupon_envelope_pluxee_incentive_new.png',
  'wlozenie-kuponow-do-kopert-ozdobnych': 'ESPI_envelope_put_in_pluxee_incentive_new.png',
};

const BN_INCENTIVE_IMAGE_BY_CATEGORY: ConfectionImageByCategory = {
  [NDE_CONFECTION.ETUI]: 'ESPI_etui_swieta_BN26.png',
  [NDE_CONFECTION.ETUI_PUT_IN]: 'ESPI_etui_put_in_BN26.png',
  [NDE_CONFECTION.ETUI_PERSONALISATION]: 'ESPI_etui_perso_BN26.png',
  [NDE_CONFECTION.ENVELOPE_PERSONALISATION]: 'ESPI_envelope_perso_BN26.png',
};

const PRODUCT_ID_INCENTIVE_IMAGE_OVERRIDES_BY_CATEGORY: Record<
  ParentProductId,
  ConfectionImageByCategory
> = {
  30702: BN_INCENTIVE_IMAGE_BY_CATEGORY,
  30937: BN_INCENTIVE_IMAGE_BY_CATEGORY,
};

const PRODUCT_ID_INCENTIVE_IMAGE_OVERRIDES_BY_SLUG: Record<
  ParentProductId,
  ConfectionImageBySlug
> = {};

const DEFAULT_MODAL_IMAGE = 'ESPI_nde_etui_pluxee_new.png';
const DEFAULT_INCENTIVE_MODAL_IMAGE = 'ESPI_nde_etui_pluxee_incentive_new.png';

function resolveOverrideByProductId<TValue>(
  overridesByProductId: Record<ParentProductId, Record<string, TValue>>,
  productId: ParentProductId,
  key: string,
): TValue | null {
  if (!key) return null;
  const overrides = overridesByProductId[productId];
  if (!overrides) return null;
  return overrides[key] || null;
}

const getModalTypeOverrideByProductIdAndCategory = (
  productId: ParentProductId,
  category: ConfectionCategory,
): string | null => resolveOverrideByProductId(PRODUCT_ID_MODAL_TYPE_OVERRIDES_BY_CATEGORY, productId, category);

const getModalImageOverrideByProductIdAndModalType = (
  productId: ParentProductId,
  modalType: ModalType,
): string | null => resolveOverrideByProductId(PRODUCT_ID_MODAL_IMAGE_OVERRIDES_BY_MODAL_TYPE, productId, modalType);

const getIncentiveImageOverrideByProductIdAndCategory = (
  productId: ParentProductId,
  category: ConfectionCategory,
): string | null => resolveOverrideByProductId(PRODUCT_ID_INCENTIVE_IMAGE_OVERRIDES_BY_CATEGORY, productId, category);

const getIncentiveImageOverrideByProductIdAndSlug = (
  productId: ParentProductId,
  slug: ConfectionSlug,
): string | null => resolveOverrideByProductId(PRODUCT_ID_INCENTIVE_IMAGE_OVERRIDES_BY_SLUG, productId, slug);

export const resolveConfectionModalType = (
  productId: ParentProductId,
  category: ConfectionCategory,
): ModalType | null => {
  const overrideModalType = getModalTypeOverrideByProductIdAndCategory(productId, category);
  if (overrideModalType) return overrideModalType;
  return DEFAULT_MODAL_TYPE_BY_CATEGORY[category] || null;
};

export const resolveConfectionModalImageName = (
  productId: ParentProductId,
  modalType: ModalType,
): string => {
  const overrideImage = getModalImageOverrideByProductIdAndModalType(productId, modalType);
  if (overrideImage) return overrideImage;
  return MODAL_IMAGE_BY_MODAL_TYPE[modalType] || DEFAULT_MODAL_IMAGE;
};

export const resolveConfectionIncentiveModalImageName = (
  productId: ParentProductId,
  category: ConfectionCategory,
  slug: ConfectionSlug,
): string => {
  const slugOverride = getIncentiveImageOverrideByProductIdAndSlug(productId, slug);
  if (slugOverride) return slugOverride;

  const categoryOverride = getIncentiveImageOverrideByProductIdAndCategory(productId, category);
  if (categoryOverride) return categoryOverride;

  const imageFromSlug = INCENTIVE_MODAL_IMAGE_BY_SLUG[slug];
  if (imageFromSlug) return imageFromSlug;

  return INCENTIVE_MODAL_IMAGE_BY_CATEGORY[category] || DEFAULT_INCENTIVE_MODAL_IMAGE;
};
